import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { navLinks } from "../content/site";
import { useCart } from "../context/CartContext";
import { Logo } from "./Logo";
import { EASE } from "./Reveal";

export function Nav() {
  const { count, setIsOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`sticky top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
          scrolled ? "border-stone-900/10 bg-[#FBF9F5]/90" : "border-transparent bg-[#FBF9F5]/60"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `group relative font-mono text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                    isActive ? "text-emerald-900" : "text-stone-700 hover:text-stone-950"
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-emerald-900 transition-[width] duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="cart-drawer-toggle"
              onClick={() => setIsOpen(true)}
              className="relative flex items-center gap-2 border border-stone-900 px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50"
              aria-label="Open bag"
            >
              <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Bag</span>
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-900 text-[10px] font-semibold text-stone-50"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="p-2 text-stone-900 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-nav"
            className="fixed inset-0 z-[60] flex flex-col bg-[#FBF9F5]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <div className="flex items-center justify-between px-4 py-4 sm:px-8">
              <Logo />
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="p-2 text-stone-900"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block border-b border-stone-900/10 py-4 font-serif text-4xl font-light text-stone-900 transition-colors duration-300 hover:text-emerald-900"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="px-8 pb-10 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500">
              Wellness, Simply.
              <img
                src="/assets/favicon.png"
                alt=""
                aria-hidden="true"
                className="ml-1.5 inline-block h-[1.1em] w-auto -translate-y-[0.1em]"
              />
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
